import api from './api';

export type DealKind = 'RECURRING' | 'ONE_TIME';
export type DealStatus = 'ACTIVE' | 'PAUSED' | 'ENDED';

export interface DealUsage {
  includedHours: number;
  usedHours: number;
  coveredHours: number;
  overageHours: number;
  remainingHours: number;
  overageRate: number;
  periodLabel: string;
}

export interface DealCompany {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  logo?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    zipCode?: string;
  };
  billing?: {
    hourlyRate?: number;
    contactName?: string;
  };
}

export interface Deal {
  _id: string;
  company: DealCompany | string;
  name: string;
  notes?: string;
  kind: DealKind;
  quantity: number;
  unitPrice: number;
  support?: boolean;
  overageRate?: number;
  clientSignature?: string;
  autoInvoice: boolean;
  status: DealStatus;
  usage?: DealUsage;
  createdAt: string;
  updatedAt: string;
}

export interface DealPayload {
  company: string;
  name: string;
  notes?: string;
  kind: DealKind;
  quantity: number;
  unitPrice: number;
  support?: boolean;
  overageRate?: number;
  clientSignature?: string;
  autoInvoice: boolean;
  status?: DealStatus;
}

export interface DealParty {
  companyName: string;
  displayName?: string;
  issuer?: string;
  contactName?: string;
  signerName?: string;
  email?: string;
  phone?: string;
  taxId?: string;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  logo?: string;
}

export interface DealDocument {
  deal: Deal;
  issuer: DealParty | null;
  client: DealParty | null;
}

const unwrap = <T,>(response: any): T => response.data.data ?? response.data;

export const dealService = {
  async list(companyId?: string, status?: DealStatus): Promise<Deal[]> {
    const response = await api.get('/deals', {
      params: {
        companyId: companyId || undefined,
        status: status || undefined
      }
    });
    return unwrap<Deal[]>(response);
  },

  async get(id: string): Promise<DealDocument> {
    const response = await api.get(`/deals/${id}`);
    return {
      deal: unwrap<Deal>(response),
      issuer: response.data.issuer || null,
      client: response.data.client || null
    };
  },

  async create(payload: DealPayload): Promise<Deal> {
    const response = await api.post('/deals', payload);
    return unwrap<Deal>(response);
  },

  async update(id: string, payload: Partial<DealPayload>): Promise<Deal> {
    const response = await api.put(`/deals/${id}`, payload);
    return unwrap<Deal>(response);
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/deals/${id}`);
  }
};
